import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FormFieldContext } from '../form-field/form-field.context';

let nextUniqueId;
nextUniqueId = 0;

// eslint-disable-next-line complexity
/**
 * @typedef Props
 * @prop {() => void} [onChange]
 * @prop {string} [max]
 * @prop {string} [min]
 * @prop {string | number | number[]} [value]
 * @prop {string} [className]
 * @prop {boolean} [disabled]
 * @prop {string} [id]
 * @prop {boolean} [showMinMax]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifSlider = (props) => {
  const {
    onChange = null,
    max = '100',
    min = '0',
    value = '',
    className = '',
    disabled = false,
    id = '',
    showMinMax = true,
    ...other
  } = props;
  const mouseDownX = useRef();
  const isValueArray = Array.isArray(value);
  const isMinValNeg = min < 0;
  const [minVal, setMinVal] = useState(isValueArray ? value[0] : '');
  const [maxVal, setMaxVal] = useState(isValueArray ? value[1] : value);
  const [perc, setPerc] = useState({
    max: '',
    min: ''
  });

  const rangeRef = useRef(null);
  const sliderRef = useRef(null);
  const minimumValueRef = useRef(null);
  const maximumValueRef = useRef(null);
  const minimumThumbRef = useRef(null);
  const maximumThumbRef = useRef(null);

  const formFieldContext = useContext(FormFieldContext);

  useEffect(() => {
    formFieldContext.setFormElementType('slider');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-slider-${nextUniqueId++}`);
  }, []);

  // Convert to percentage
  const getPercent = useCallback(
    newValue => Math.round(newValue - Number(min)),
    [min, max]
  );

  // Get width of slider
  const getWidth = useCallback(
    newWidth => Math.round((newWidth / 100) * (Number(max) - Number(min)) + Number(min)),
    [min, max]
  );

  useEffect(() => {
    const { step = 1 } = { ...other };
    const minCalcVal = isValueArray && typeof Number(minVal) === 'number' ? minVal : min;
    const minPercent = jumpByStep(getPercent(minCalcVal), step);
    const maxPercent = jumpByStep(getPercent(maxVal), step);

    setPerc({
      max: maxPercent,
      min: minPercent
    });

    if (!rangeRef.current) {
      return;
    }

    // The percentage difference of the maximum and the minimum of the values multiplied by 100
    // divided the difference of the min and max which is 100% of the slider bar
    const size = ((maxPercent - minPercent) * 100 / (max - min));
    const width = `${maxPercent * 100 / (max - min)}%`;

    if (isMinValNeg && !isValueArray) {
      rangeRef.current.style.left = '0%';
      rangeRef.current.style.width = width;
      maximumValueRef.current.style.left = width;
    } else if (isMinValNeg && isValueArray) {
      rangeRef.current.style.left = `${minPercent * 100 / (max - min)}%`;
      rangeRef.current.style.width = `${size}%`;
      maximumValueRef.current.style.left = width;
    } else {
      rangeRef.current.style.left = `${minPercent * 100 / max}%`;
      rangeRef.current.style.width = `${size}%`;
      // The left margin of the maximum value when there is a minimum value in the slider, is
      // equal to the sum of the maximum and the minimum
      maximumValueRef.current.style.left = `${size + minVal}%`;
    }
  }, [minVal, maxVal]);

  // jumpByStep is a function created to detect the jumps between points in the slider when there
  // are steps greater than 1
  const jumpByStep = (percentage, step) => {
    const valueToMultiply = Math.floor(percentage / step);

    // if the percentage chosen between two points is greater than 0.5, the value of the point on
    // the slider is considered as the highest
    const middleRange = percentage / step - valueToMultiply >= 0.5 ? valueToMultiply + 1 : valueToMultiply;

    const maxValue = isMinValNeg ? max - min : max;
    // if the percentage of the maximum value exceeds the maximum of the slider, the last valid
    // value of the slider is considered as the value

    return middleRange * step > maxValue ? (middleRange - 1) * step : middleRange * step;
  };

  // handle click on double range input as in primitive input range
  const handleClickTrack = event => {
    const mouseUpX = event.clientX;
    // if not click, exit
    if (
      mouseUpX > mouseDownX.current ||
      mouseUpX < mouseDownX.current
    ) {
      return;
    }

    const xPosition = event.clientX - sliderRef.current.getBoundingClientRect().left;

    let sliderPercentageThumb = Math.round(xPosition * 100 / sliderRef.current.clientWidth);

    if (sliderPercentageThumb <= 0) {
      sliderPercentageThumb = 0;
    } else if (sliderPercentageThumb >= 100) {
      sliderPercentageThumb = 100;
    }

    const minValueDiff = minimumValueRef.current ? Math.abs(sliderPercentageThumb - parseInt(minimumValueRef.current.style.left, 10)) : 100;
    const maxValueDiff = Math.abs(sliderPercentageThumb - parseInt(maximumValueRef.current.style.left, 10));

    if (minValueDiff > maxValueDiff) {
      maximumThumbRef.current.focus();
      setMaxVal(getWidth(sliderPercentageThumb));

      onChange(isValueArray ? { target: { value: [minVal, getWidth(sliderPercentageThumb)] } } : { target: { value: getWidth(sliderPercentageThumb).toString() } });
    } else {
      minimumThumbRef.current.focus();
      setMinVal(getWidth(sliderPercentageThumb));

      onChange({ target: { value: [getWidth(sliderPercentageThumb), maxVal] } });
    }
  };

  const handleChangeMin = event => {
    const newValue = Math.min(Number(event.target.value), maxVal - 1);
    setMinVal(newValue);

    onChange({ target: { value: [newValue, maxVal] } });
  };

  const handleChangeMax = event => {
    const newValue = Math.max(Number(event.target.value), minVal + 1);
    setMaxVal(newValue);

    onChange({ target: { value: [minVal, newValue] } });
  };

  const handleChange = event => {
    setMaxVal(event.target.value);

    onChange(event);
  };

  const classes = classnames({
    'motif-slider': true,
    'motif-slider-disabled': disabled,
    [className]: true
  });

  const sliderClasses = classnames({
    'motif-range-slider': true,
    'motif-range-slider-disabled': disabled
  });

  const handleChangeRange = (evt) => {
    isValueArray ? handleChangeMax(evt) : handleChange(evt);
  };

  const temporalMin = (Number.isFinite(perc.min)) ? perc.min : minVal;
  const temporalMax = (Number.isFinite(perc.max)) ? perc.max : maxVal;

  return (
    <div className={classes} data-motif-slider>
      <div
        ref={sliderRef}
        className={sliderClasses}
        onMouseDown={(event) => {
          mouseDownX.current = event.clientX;
        }}
        onClick={handleClickTrack}
      >
        {isValueArray && (
          <input
            className="motif-slider-input thumb-left"
            disabled={disabled}
            max={max}
            min={min}
            onChange={handleChangeMin}
            ref={minimumThumbRef}
            type="range"
            value={isMinValNeg ? temporalMin + Number(min) : temporalMin}
            {...other}
          />
        )}
        <input
          className="motif-slider-input thumb-right"
          disabled={disabled}
          id={formFieldContext.id}
          max={max}
          min={min}
          onChange={handleChangeRange}
          ref={maximumThumbRef}
          type="range"
          value={min > 0 ? maxVal : temporalMax + Number(min)}
          {...other}
        />
        <div className="motif-slider-value-wrap">
          {isValueArray && (
            <div
              className="motif-slider-value"
              ref={minimumValueRef}
              style={{ left: `${isMinValNeg ? temporalMin * 100 / (max - min) : temporalMin}%` }}
            >
              {Number(temporalMin) + Number(min)}
            </div>
          )}

          <div
            className="motif-slider-value"
            ref={maximumValueRef}
          >
            {Number(temporalMax) + Number(min)}
          </div>
        </div>

        <div className="motif-range-slider-track" />

        <div ref={rangeRef} className="motif-range-slider-range" />
      </div>

      {showMinMax && (
        <div className="motif-slider-min-max">
          <div className="motif-slider-min">{min}</div>
          <div className="motif-slider-max">{max}</div>
        </div>
      )}
    </div>
  );
};

MotifSlider.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  max: PropTypes.string,
  min: PropTypes.string,
  onChange: PropTypes.func,
  showMinMax: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number)
  ])
};

export { MotifSlider };
